import { useEffect, useState } from 'react';

import { StatusDropdown } from '@/components/ui/status-dropdown';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog';
import { useTranslation } from '@hooks/useTranslation';
import { useUpdateResaleItemStatus } from '@hooks/resaleItems';

import { createResaleItemStatusDropdownConfig } from '../resale-item-status-config';
import type { ResaleItemStatus } from '../types';

interface ResaleItemStatusDropdownProps {
  resaleItemId: number;
  active: boolean;
}

export function ResaleItemStatusDropdown({ resaleItemId, active }: ResaleItemStatusDropdownProps) {
  const { t } = useTranslation();
  const [currentActive, setCurrentActive] = useState(active);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const updateStatus = useUpdateResaleItemStatus();

  useEffect(() => {
    setCurrentActive(active);
  }, [active]);

  const config = createResaleItemStatusDropdownConfig({
    active: t('common.status.active'),
    inactive: t('common.status.inactive'),
  });

  const value: ResaleItemStatus = currentActive ? 'active' : 'inactive';

  const handleChange = (next: ResaleItemStatus) => {
    const nextActive = next === 'active';
    if (nextActive === currentActive) return;

    if (!nextActive) {
      setConfirmDeactivateOpen(true);
      return;
    }

    updateStatus.mutate(
      { id: resaleItemId, active: true },
      { onSuccess: () => setCurrentActive(true) },
    );
  };

  const handleConfirmDeactivate = () => {
    setConfirmDeactivateOpen(false);
    updateStatus.mutate(
      { id: resaleItemId, active: false },
      { onSuccess: () => setCurrentActive(false) },
    );
  };

  return (
    <>
      <StatusDropdown
        value={value}
        config={config}
        onChange={handleChange}
        disabled={updateStatus.isPending}
      />
      <ConfirmationDialog
        open={confirmDeactivateOpen}
        onOpenChange={setConfirmDeactivateOpen}
        onConfirm={handleConfirmDeactivate}
        title={t('status_dropdown.deactivate_confirm_title')}
        message={t('status_dropdown.deactivate_confirm_message')}
        confirmLabel={t('status_dropdown.deactivate_confirm_action')}
        cancelLabel={t('status_dropdown.deactivate_cancel_action')}
        variant="destructive"
      />
    </>
  );
}
